import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtINR } from "../lib/constants";
import { Briefcase, FileText, Receipt, Users, TrendingUp, Activity, ListTodo, CheckCircle2, AlertTriangle, BarChart3 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const KPI = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-6 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-200">
    <div className="flex items-start justify-between mb-6">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold">{label}</span>
    </div>
    <div className="text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, p, t] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/projects"),
          api.get("/tasks"),
        ]);
        setStats(s.data);
        setProjects(p.data.slice(0, 5));
        setTasks(t.data.slice(0, 6));
        // Try fetching admin KPIs (super_admin only)
        try { const r = await api.get("/admin/dashboard-kpis"); setKpis(r.data); }
        catch (e) { console.debug("Admin KPIs unavailable for this role", e?.response?.status); }
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!stats) return <div className="text-stone-500">Loading...</div>;

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Control Room</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>
            Operations overview
          </h1>
          <p className="text-stone-500 mt-2">Live snapshot of projects, finance and team performance.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500">Today</div>
          <div className="font-semibold text-stone-800">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </div>

      {/* Management KPIs (super_admin only) */}
      {kpis && (
        <section data-testid="management-kpis">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#72BE44] font-bold">Management Dashboard · KPIs</div>
            <div className="text-[10px] text-stone-400">Updated {(kpis.generated_at || "").slice(11, 16)}</div>
          </div>

          {/* Funnel */}
          <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4">
            <div className="text-sm font-semibold text-[#0F2A4A] mb-3">Conversion Funnel <span className="text-[10px] font-normal text-stone-400 ml-2">click any stage to open its list</span></div>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
              <FunnelCell testid="funnel-leads" label="Leads" value={kpis.funnel.leads} onClick={() => nav("/leads")} />
              <FunnelArrow value={`${Math.min(kpis.funnel.lead_to_proposal_pct, 100)}%`} />
              <FunnelCell testid="funnel-proposals" label="Proposals" value={kpis.funnel.proposals} sub={`${kpis.funnel.proposals_accepted} accepted`} onClick={() => nav("/proposals")} />
              <FunnelArrow value={`${Math.min(kpis.funnel.proposal_to_wo_pct, 100)}%`} />
              <FunnelCell testid="funnel-workorders" label="Work Orders" value={kpis.funnel.work_orders} onClick={() => nav("/work-orders")} />
              <FunnelArrow value={`${Math.min(kpis.funnel.wo_to_project_pct, 100)}%`} />
              <FunnelCell testid="funnel-projects" label="Projects" value={kpis.funnel.projects} sub={`EC ${kpis.funnel.ec_approved} · CTE ${kpis.funnel.cte_approved} · CTO ${kpis.funnel.cto_approved}`} accent onClick={() => nav("/projects")} />
            </div>
          </div>

          {/* Financial + Delayed + Workload */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <button
              type="button"
              onClick={() => nav("/work-orders")}
              data-testid="kpi-booked-revenue"
              className="text-left bg-white border border-stone-200 rounded-xl p-5 border-l-4 border-l-emerald-600 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Booked Revenue (WO)</div>
              <div className="text-2xl font-bold text-[#0F2A4A] mt-1">{fmtINR(kpis.booked_revenue)}</div>
              <div className="text-xs text-stone-500 mt-1">Pipeline {fmtINR(kpis.pipeline_revenue)} · view work orders →</div>
            </button>
            <button
              type="button"
              onClick={() => nav("/invoices")}
              data-testid="kpi-outstanding"
              className="text-left bg-white border border-stone-200 rounded-xl p-5 border-l-4 border-l-amber-500 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Outstanding Payments</div>
              <div className="text-2xl font-bold text-amber-700 mt-1">{fmtINR(kpis.outstanding_total)}</div>
              <div className="text-xs text-stone-500 mt-1">Received {fmtINR(kpis.payments_received)} · view invoices →</div>
            </button>
            <button
              type="button"
              onClick={() => nav("/projects?delayed=1")}
              data-testid="kpi-delayed"
              className="text-left bg-white border border-stone-200 rounded-xl p-5 border-l-4 border-l-rose-500 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Delayed Projects</div>
              <div className="text-2xl font-bold text-rose-700 mt-1 flex items-center gap-2">
                {kpis.delayed_count} {kpis.delayed_count > 0 && <AlertTriangle className="w-5 h-5" />}
              </div>
              {kpis.delayed_projects.slice(0, 2).map(p => (
                <div key={p.id} className="text-[11px] text-stone-600 mt-1 truncate">{p.project_id_external || ""} · {p.project_name}</div>
              ))}
            </button>
          </div>

          {/* Team Workload */}
          <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="team-workload">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-[#0F2A4A]">Team Workload</div>
              <div className="text-[10px] text-stone-400">click a member&apos;s project count to see their projects</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-stone-500">
                  <tr className="border-b border-stone-200">
                    <th className="text-left py-2 font-semibold">Member</th>
                    <th className="text-left py-2 font-semibold">Role</th>
                    <th className="text-right py-2 font-semibold">Active Projects</th>
                    <th className="text-right py-2 font-semibold">Open Leads</th>
                    <th className="text-right py-2 font-semibold">Open Milestones</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.team_workload.map((w) => (
                    <tr
                      key={w.id}
                      data-testid={`workload-row-${w.id}`}
                      className="border-b border-stone-100 hover:bg-emerald-50/40 cursor-pointer transition"
                      onClick={() => nav(`/projects?assigned=${w.id}`)}
                    >
                      <td className="py-2 font-medium text-[#0F2A4A]">{w.name}</td>
                      <td className="py-2 text-xs text-stone-500">{w.role}</td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          data-testid={`workload-projects-${w.id}`}
                          onClick={(e) => { e.stopPropagation(); nav(`/projects?assigned=${w.id}`); }}
                          className="font-bold text-emerald-700 hover:underline"
                        >
                          {w.projects}
                        </button>
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          data-testid={`workload-leads-${w.id}`}
                          onClick={(e) => { e.stopPropagation(); nav(`/leads?assigned=${w.id}`); }}
                          className="hover:underline text-[#0F2A4A]"
                        >
                          {w.leads}
                        </button>
                      </td>
                      <td className="py-2 text-right">
                        {w.milestones > 5 ? <span className="text-rose-600 font-bold">{w.milestones}</span> : w.milestones}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Operations */}
      <section>
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">Operations</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={Briefcase} accent="bg-emerald-50 text-emerald-700" label="Active" value={stats.operations.total_projects} sub="Total projects" />
          <KPI icon={Users} accent="bg-amber-50 text-amber-700" label="Pipeline" value={stats.operations.leads} sub="Open leads" />
          <KPI icon={FileText} accent="bg-sky-50 text-sky-700" label="Fresh EC" value={stats.operations.fresh_ec} sub={`+ ${stats.operations.amendment_ec} Amendment`} />
          <KPI icon={CheckCircle2} accent="bg-stone-100 text-stone-700" label="Consents" value={stats.operations.consents} sub={`${stats.operations.pmrs} PMRs tracked`} />
        </div>
      </section>

      {/* Finance & Team */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-1">Finance</div>
              <div className="text-xl font-semibold text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Revenue & receivables</div>
            </div>
            <Link to="/invoices" className="text-xs text-emerald-700 hover:text-emerald-800 font-medium">View billing →</Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="border-l-2 border-emerald-600 pl-4">
              <div className="text-xs uppercase tracking-wider text-stone-500">Collected</div>
              <div className="text-2xl font-bold text-stone-900 mt-1">{fmtINR(stats.finance.revenue)}</div>
            </div>
            <div className="border-l-2 border-amber-500 pl-4">
              <div className="text-xs uppercase tracking-wider text-stone-500">Outstanding</div>
              <div className="text-2xl font-bold text-stone-900 mt-1">{fmtINR(stats.finance.outstanding)}</div>
            </div>
            <div className="border-l-2 border-stone-300 pl-4">
              <div className="text-xs uppercase tracking-wider text-stone-500">Total raised</div>
              <div className="text-2xl font-bold text-stone-900 mt-1">{fmtINR(stats.finance.raised)}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-1">Team</div>
          <div className="text-xl font-semibold text-stone-900 mb-6" style={{ fontFamily: "Bricolage Grotesque" }}>Task load</div>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <ListTodo className="w-4 h-4 text-amber-700" />
              <div className="flex-1 text-sm text-stone-600">Open</div>
              <div className="font-bold text-stone-900">{stats.team.open_tasks}</div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <div className="flex-1 text-sm text-stone-600">Closed</div>
              <div className="font-bold text-stone-900">{stats.team.closed_tasks}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-stone-900">Recent projects</div>
            <Link to="/projects" className="text-xs text-emerald-700 font-medium">All projects →</Link>
          </div>
          <div className="space-y-3">
            {projects.length === 0 && <div className="text-sm text-stone-500">No projects yet.</div>}
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`dashboard-project-${p.id}`}
                className="block border border-stone-200 rounded-lg p-4 hover:border-emerald-700 hover:bg-emerald-50/30 transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-stone-900">{p.project_name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{p.client_name} · {p.project_location}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">{p.service_type}</span>
                </div>
                <div className="text-xs text-stone-600 mt-2">Stage: <span className="font-medium text-stone-800">{p.current_stage || "—"}</span></div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-stone-900">Tasks queue</div>
            <Link to="/tasks" className="text-xs text-emerald-700 font-medium">All tasks →</Link>
          </div>
          <div className="space-y-2">
            {tasks.length === 0 && <div className="text-sm text-stone-500">All clear!</div>}
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-b-0">
                <div className={`w-1.5 h-1.5 rounded-full ${t.status === "Closed" ? "bg-emerald-500" : t.priority === "Critical" ? "bg-rose-500" : "bg-amber-500"}`} />
                <div className="flex-1 text-sm text-stone-700">{t.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">{t.status}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelCell({ label, value, sub, accent, onClick, testid }) {
  const cls = `rounded-lg p-3 text-center transition ${accent ? "bg-[#72BE44] text-white" : "bg-stone-50 text-[#0F2A4A]"} ${onClick ? "cursor-pointer hover:scale-[1.03] hover:shadow-md" : ""}`;
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls} disabled={!onClick}>
      <div className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[9px] opacity-75 mt-1">{sub}</div>}
    </button>
  );
}

function FunnelArrow({ value }) {
  return (
    <div className="flex flex-col items-center justify-center text-[10px] uppercase tracking-wider text-stone-500 font-bold">
      <div className="text-[#72BE44] text-base">→</div>
      <div>{value}</div>
    </div>
  );
}

